/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { DateFormatInternal } from "abweb/utils/date/internal/DateFormatInternal";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { StringUtils } from "abweb/utils/StringUtils";
const LOG = LogUtils.getLogger(__filename);
export class ValueFormatAttributesUtils {
  constructor() {
    // static class
  }

  /**
   * Render the value as a Number according to the formatAttribs.
   * @param value
   * @param formatAttribs Optional - if not supplied, defaults to formatting with full decimal precision and thousands separator.
   * @param saveFormatter Optional - defaults to false.  if true and formatAttribs is defined, the calculated formatter
   * will be saved to the formatAttribs.numberFormatter attribute.  Caller should only set to true if the other
   * formatAttribs will remain constant on subsequent calls (expected case), or caller takes responsibility for clearing the
   * formatAttribs.dateTimeFormatter when any of the underlying formatting attributes change
   * (which will force a recalculation on subsequent calls).
   *
   * @returns It will return an empty string if the value is null, undefined, or isNaN.
   * If formatAttribs is not supplied, defaults to formatting as a full decimal with thousands separator.
   * If the value is a valid number, and formatAttribs are supplied, it will be formatted according to the formatAttribs argument,
   * see IValueFormatAttributes doc for more info about the various numeric formatting options.
   */
  static formatNumberAsString(value, formatAttribs, saveFormatter = false) {
    if (value == null || isNaN(value)) {
      return "";
    }
    let numFormatter = formatAttribs?.numberFormatter;
    if (!numFormatter) {
      numFormatter = ValueFormatAttributesUtils.buildNumberFormatter(formatAttribs);
      if (saveFormatter && formatAttribs) {
        formatAttribs.numberFormatter = numFormatter;
      }
    }
    return numFormatter(value);
  }
  static buildNumberFormatter(formatAttribs) {
    if (formatAttribs == null) {
      // Default format: full precision decimal with thousands separator
      return NumberUtils.buildNumberFormatter();
    }
    if (formatAttribs.numberFormatter) {
      return formatAttribs.numberFormatter;
    }
    if (ValueFormatAttributesUtils.isEqualsIgnoresCase("Percentage", formatAttribs.valueFormatType)) {
      // if percentage, always does percentage.  Previously use1000Abbr would take precedence if true.
      return NumberUtils.buildPercentageNumberFormatter(formatAttribs.numDecimalPlaces, formatAttribs.use1000Separator);
    }

    // Note documented IValueFormatAttributes.valueFormatType says it should be set to Currency,
    //   in which case it will grab currency value from valueFormatOptions.
    // HOWEVER - existing currency tests in DataSourceValue set valueFormatOptions=USD
    //   but did not set valueFormatType to Currency and still expected currency formatting.
    //   So for backward compatibility, do  not check that valueFormatType===Currency.
    const currency = formatAttribs.valueFormatOptions ?? null;
    return formatAttribs.use1000Abbr ? NumberUtils.build1000AbbrNumberFormatter(formatAttribs.numDecimalPlaces, formatAttribs.use1000Separator, currency) : NumberUtils.buildNumberFormatter(formatAttribs.numDecimalPlaces, formatAttribs.use1000Separator, currency);
  }

  /**
   * @param value Date instance
   * @param formatAttribs
   * @param saveFormatter Optional - defaults to false.  if true and formatAttribs is defined, the calculated formatter
   * will be saved to the formatAttribs.dateTimeFormatter attribute.  Caller should only set to true if the other
   * formatAttribs will remain constant on subsequent calls (expected case), or caller takes responsibility for clearing the
   * formatAttribs.dateTimeFormatter when any of the underlying formatting attributes change
   * (which will force a recalculation on subsequent calls).
   * @returns empty string value is null or undefined.
   * if formatAttribs are undefined, default to DATE_SHORT localized formatting (e.g. 10/31/2000 for en-US).
   */
  static formatDateAsString(value, formatAttribs, saveFormatter = false) {
    return ValueFormatAttributesUtils.formatRawDateAsString(value, formatAttribs, DateTimeUtils.DATE_SHORT, saveFormatter);
  }

  /**
   * @param value Date instance
   * @param formatAttribs Controls formatting of the Date value
   * @param saveFormatter Optional - defaults to false.  if true and formatAttribs is defined, the calculated formatter
   * will be saved to the formatAttribs.dateTimeFormatter attribute.  Caller should only set to true if the other
   * formatAttribs will remain constant on subsequent calls (expected case), or caller takes responsibility for clearing the
   * formatAttribs.dateTimeFormatter when any of the underlying formatting attributes change
   * (which will force a recalculation on subsequent calls).
   * @returns empty string value is null or undefined.
   * if formatAttribs are undefined, default to DATETIME_MEDIUM localized formatting (e.g. '10/31/2000, 1:30:00 PM' for en-US).
   */
  static formatDatetimeAsString(value, formatAttribs, saveFormatter = false) {
    return ValueFormatAttributesUtils.formatRawDateAsString(value, formatAttribs, DateTimeUtils.DATETIME_WITH_SECONDS, saveFormatter);
  }
  static formatRawDateAsString(value, formatAttribs, defaultFormatter, saveFormatter) {
    if (!value) {
      return "";
    }
    let dtFormatter = formatAttribs?.dateTimeFormatter;
    if (!dtFormatter) {
      dtFormatter = ValueFormatAttributesUtils.buildDateTimeFormatter(formatAttribs, defaultFormatter);
      if (saveFormatter && formatAttribs) {
        formatAttribs.dateTimeFormatter = dtFormatter;
      }
    }
    return dtFormatter.format(value);
  }
  static buildDateTimeFormatter(formatAttribs, defaultFormatter) {
    if (formatAttribs == null) {
      // Default format: localized datetime to second precision
      return defaultFormatter;
    }
    if (formatAttribs.dateTimeFormatter) {
      return formatAttribs.dateTimeFormatter;
    }

    // Below formatting is relatively inefficient as potentially create a new number formatter for every rendered value.
    // Use formatAttribs.dateTimeFormatter above to be (much) more efficient,
    // as the cost of creating formatter typically dwarfs cost of using it)

    if (formatAttribs.valueFormatOptions) {
      // Per ABSDK-5402, valueFormatOptions is now assumed to be in Luxon format
      return DateFormatInternal.buildAbInitioDateTimeFormatter(formatAttribs.valueFormatOptions, false);
    }
    switch (formatAttribs.valueFormatType?.toUpperCase()) {
      case "DATE":
        return DateTimeUtils.DATE_SHORT;
      case "DATETIME":
        return DateTimeUtils.DATETIME_WITH_SECONDS;
      default:
        // Just use the default formatter if valueFormatType is not specified
        return defaultFormatter;
    }
  }
  static isEqualsIgnoresCase(value1, value2) {
    return value2 == null ? false : StringUtils.equalsIgnoreCase(value1, value2);
  }
}
//# sourceMappingURL=ValueFormatAttributesUtils.js.map
